import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import axios from "axios";
import * as XLSX from "xlsx";
import { GlobalState } from "context/GlobalContext";

const FETCH_NEW_STORES_ENDPOINT = "/api/pos_reco/get_new_stores";

const dialogGridSx = {
	height: "100%",
	width: "100%",
	border: "1px solid",
	borderColor: "divider",
	borderRadius: 2,
	bgcolor: "background.paper",
	"& .MuiDataGrid-toolbarContainer": {
		padding: "10px 12px 6px",
		borderBottom: "1px solid",
		borderColor: "divider",
		gap: 1,
	},
	"& .MuiDataGrid-toolbarContainer .MuiButton-root": {
		minWidth: "auto",
		padding: "4px 8px",
		borderRadius: 1.5,
		color: "text.secondary",
		fontSize: "0.75rem",
		fontWeight: 500,
	},
	"& .MuiDataGrid-toolbarContainer .MuiInputBase-root": {
		borderRadius: 1.5,
		backgroundColor: "grey.50",
	},
	"& .MuiDataGrid-columnHeaders": {
		backgroundColor: "grey.50",
		borderBottom: "1px solid",
		borderColor: "divider",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		fontWeight: 500,
		color: "text.secondary",
		textTransform: "uppercase",
		fontSize: "0.7rem",
		letterSpacing: "0.4px",
	},
	"& .MuiDataGrid-cell": {
		borderBottom: "1px solid",
		borderColor: "divider",
		fontSize: "0.82rem",
		color: "text.primary",
	},
	"& .MuiDataGrid-columnSeparator": {
		display: "none",
	},
};

const downloadWorkbook = (rows) => {
	const worksheet = XLSX.utils.json_to_sheet(rows);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Store Master");
	XLSX.writeFile(workbook, "store_master_new_stores.xlsx");
};

const FetchNewStoresDialog = ({ open, onClose }) => {
	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!open) return;

		const fetchStores = async () => {
			setLoading(true);
			try {
				const response = await axios.get(FETCH_NEW_STORES_ENDPOINT);
				const data = Array.isArray(response.data) ? response.data : response.data?.result || response.data?.stores || [];
				setRows(
					data.map((row, index) => ({
						id: row?.id ?? row?.site_code ?? row?.store_code ?? `new-store-${index}`,
						store_code: row?.site_code ?? row?.store_code ?? "",
						store_full_name:
							row?.full_name ?? row?.Site ?? row?.short_name ?? row?.store_full_name ?? row?.store_name ?? "",
					})),
				);
			} catch (error) {
				setRows([]);
				setSnackBarOpen(true);
				setSnackBar({
					...snackBar,
					message: "Unable to fetch new stores",
					severity: "error",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			} finally {
				setLoading(false);
			}
		};

		fetchStores();
	}, [open]);

	const columns = useMemo(
		() => [
			{ field: "store_code", headerName: "Store Code", minWidth: 180, flex: 0.7 },
			{ field: "store_full_name", headerName: "Store Full Name", minWidth: 420, flex: 1.3 },
		],
		[],
	);

	const handleDownload = () => {
		const formattedRows = rows.map((row) => ({
			full_name: row.store_full_name || "",
			short_name: "",
			site_code: row.store_code || "",
			upi_id_1: "",
			upi_id_2: "",
			upi_id_3: "",
			card_tid_1: "",
			card_tid_2: "",
			amex_id: "",
			phonepe_id: "",
			pay_by_link: "",
			magicpin_id: "",
			zomato_id: "",
			Status: "",
		}));

		downloadWorkbook(formattedRows);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Fetch New Stores
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Review newly fetched stores and download them in store master format.
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Button
						variant="outlined"
						size="small"
						onClick={handleDownload}
						disabled={!rows.length}
						startIcon={<DownloadRoundedIcon />}>
						Download Format
					</Button>
					<IconButton onClick={onClose}>
						<CloseRoundedIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ pb: 3 }}>
				<Box sx={{ height: "65vh", width: "100%", pt: 1 }}>
					<DataGrid
						rows={rows}
						columns={columns}
						loading={loading}
						disableRowSelectionOnClick
						initialState={{
							pagination: { paginationModel: { pageSize: 25 } },
						}}
						pageSizeOptions={[10, 25, 50]}
						slots={{
							noRowsOverlay: () => (
								<Typography variant="body2" sx={{ mt: 2 }}>
									No new stores found
								</Typography>
							),
						}}
						slotProps={{
							toolbar: { showQuickFilter: true },
						}}
						sx={dialogGridSx}
					/>
				</Box>
			</DialogContent>
		</Dialog>
	);
};

export default FetchNewStoresDialog;
